import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Form, Input, Button, message, Card, Typography } from 'antd';
import { Link } from 'react-router';
import { FORGOT_PASSWORD } from '../../graphql/mutations/mutations';
import { AuthCardLayout } from '../../layouts/AuthCardLayout';

const { Title } = Typography;

interface ForgotPasswordFormValues {
  email: string;
}

const EmailSent: React.FC<{ email: string }> = ({ email }) => {
  return (
    <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
      <Title level={2}>Check your email</Title>
      <p>We&apos;ve sent a password reset link to {email}</p>
      <Link to="/login">
        <Button type="primary" style={{ width: '100%' }}>
          Return to login
        </Button>
      </Link>
    </Card>
  );
};

const ForgotPasswordForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState<string>();

  const [forgotPassword] = useMutation(FORGOT_PASSWORD, {
    onCompleted: data => {
      if (data.forgotPassword) {
        message.success('A link to reset your password has been sent to your mailbox');
      } else {
        message.error('Failed to send reset link');
      }
      setResetEmail(form.getFieldValue('email'));
      setLoading(false);
    },
    onError: error => {
      message.error(error.message || 'Failed to send reset link');
      setLoading(false);
    },
  });

  const handleSubmit = (values: ForgotPasswordFormValues) => {
    setLoading(true);
    forgotPassword({
      variables: {
        email: values.email,
      },
    });
  };

  if (resetEmail) {
    return <EmailSent email={resetEmail} />;
  }

  return (
    <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
      <Title level={2}>Reset your password</Title>

      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ email: '' }}>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}>
          <Input placeholder="your@email.com" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 'unset' }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ width: '100%', marginBottom: '10px' }}>
            Reset password
          </Button>
          Remember your password? <Link to="/login">Log in</Link>
        </Form.Item>
      </Form>
    </Card>
  );
};

export const ForgotPassword: React.FC = () => {
  return (
    <AuthCardLayout>
      <ForgotPasswordForm />
    </AuthCardLayout>
  );
};
